import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { Box, Chip, FormHelperText, FormLabel, Grid, Paper, Typography } from '@mui/material'
import { PDFPreview } from './PDFViewer'
import DescriptionIcon from '@mui/icons-material/Description'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'

const baseStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  transition: 'border .3s ease-in-out, background-color .3s',
}

const activeStyle = { borderColor: '#2196f3' }
const acceptStyle = { borderColor: '#00e676' }
const rejectStyle = { borderColor: '#ff1744' }
const hoverStyle = { backgroundColor: '#eeeeee' }

const FileField = React.forwardRef(
  (
    { inputProps = {}, label, error, helperText, required, previewFullWidth = false, ...rest },
    ref
  ) => {
    const [files, setFiles] = useState([])
    const [previewFiles, setPreviewFiles] = useState([])

    const localRef = useRef(null)
    if (!ref) ref = localRef

    const handlePreviewFiles = (newFiles) => {
      previewFiles.forEach((file) => URL.revokeObjectURL(file.preview))

      const newPreviewFiles = newFiles.map((file) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      )
      setPreviewFiles(newPreviewFiles)
    }

    const onDrop = useCallback(
      (acceptedFiles) => {
        if (!acceptedFiles || acceptedFiles.length === 0) return

        const newFiles = inputProps.multiple ? [...files, ...acceptedFiles] : [acceptedFiles[0]]

        const uniqueFiles = Array.from(
          new Map(newFiles.map((f) => [f.name + f.lastModified, f])).values()
        )

        setFiles(uniqueFiles)
        handlePreviewFiles(uniqueFiles)
      },
      [inputProps.multiple, files]
    )

    useEffect(() => {
      const initFiles = inputProps.value || inputProps.initialvalue || []
      if (initFiles.length) {
        setFiles(initFiles)
        handlePreviewFiles(initFiles)
      }

      return () => {
        previewFiles.forEach((file) => URL.revokeObjectURL(file.preview))
      }
    }, [])

    useEffect(() => {
      inputProps.onChange?.({ target: { files } })
    }, [files])

    const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } = useDropzone({
      onDrop,
      accept: inputProps.accept,
      multiple: inputProps.multiple,
    })

    const [hover, setHover] = useState(false)

    const style = useMemo(
      () => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {}),
        ...(hover ? hoverStyle : {}),
      }),
      [hover, isDragActive, isDragReject, isDragAccept]
    )

    const fileInputProps = getInputProps()

    const removeFile = (index) => {
      const newFiles = [...files]
      newFiles.splice(index, 1)
      setFiles(newFiles)
      handlePreviewFiles(newFiles)
    }

    const addFiles = (e) => {
      const fileList = Array.from(e.target.files)
      const newFiles = inputProps.multiple ? [...files, ...fileList] : [fileList[0]]

      const uniqueFiles = Array.from(
        new Map(newFiles.map((f) => [f.name + f.lastModified, f])).values()
      )

      setFiles(uniqueFiles)
      handlePreviewFiles(uniqueFiles)
    }

    const thumbs = (
      <Grid
        container
        spacing={2}
        direction='row'
        justifyContent={inputProps.multiple ? undefined : 'center'}
      >
        {previewFiles.map((file, index) => (
          <Grid
            item
            xs={previewFullWidth ? 12 : 6}
            sm={previewFullWidth ? 12 : 4}
            md={previewFullWidth ? 12 : 3}
            key={index}
          >
            <Box
              display='flex'
              position='relative'
              flexDirection='column'
              alignItems='center'
              zIndex={100}
              width='100%'
            >
              <Paper square>
                {file.type === 'application/pdf' ? (
                  <PDFPreview file={file.preview} width={64} />
                ) : ['video/mp4', 'video/ogg'].includes(file.type) ? (
                  <video style={{ minWidth: 64, maxWidth: '100%' }} controls>
                    <source src={file.preview} type={file.type} />
                  </video>
                ) : file.preview ? (
                  <img
                    style={{ minWidth: 64, maxWidth: '100%' }}
                    src={file.preview}
                    alt={file.name}
                  />
                ) : (
                  <DescriptionIcon fontSize='large' />
                )}
              </Paper>
              <Box mt={1}>
                <Chip label={file.name} variant='outlined' onDelete={() => removeFile(index)} />
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    )

    return (
      <Box display='block'>
        <FormLabel error={Boolean(error)}>
          {label} {required && '*'}
        </FormLabel>
        <Box
          position='relative'
          {...getRootProps({ style })}
          onMouseOver={() => setHover(true)}
          onMouseOut={() => setHover(false)}
        >
          <Typography variant='h3'>
            <CloudUploadIcon fontSize='inherit' />
          </Typography>
          <Typography variant='h6' align='center'>
            {inputProps.multiple ? 'Choose files...' : 'Choose a file...'}
          </Typography>

          {previewFiles?.length > 0 && (
            <Box m={1} width='100%'>
              <Paper variant='outlined'>
                <Box p={1}>{thumbs}</Box>
              </Paper>
            </Box>
          )}
          <input
            {...fileInputProps}
            ref={ref}
            type='file'
            style={{
              position: 'absolute',
              opacity: 0,
              width: '100%',
              height: '100%',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
            }}
            onChange={addFiles}
            {...rest}
          />
        </Box>
        <FormHelperText error={Boolean(error)}>{helperText}</FormHelperText>
      </Box>
    )
  }
)

export default FileField
